/* Inline variant picker for collection-grid product cards.
 *
 * When the shopper taps a swatch or size pill, this component:
 *   1. updates aria-pressed across the option row,
 *   2. recomputes the selected variant from the embedded JSON blob,
 *   3. writes the matched variant id into the sibling product-form's hidden
 *      [name="id"] input,
 *   4. refreshes the card's price block (price + compare-at strikethrough),
 *   5. disables/enables the Add-to-cart button to match availability,
 *   6. disables individual swatches whose combined-option pick would yield
 *      an unavailable variant (so the user doesn't blindly pick a dead end).
 *
 * The actual /cart/add POST is owned by <product-form> (cart.js / product-form.js)
 * elsewhere in the theme - we only switch which variant id it carries.
 */
/* Class assigned to window via `||` short-circuit so a re-loaded script
 * (Shopify sometimes re-evaluates assets when fetching section_id renders
 * after Load More fires) doesn't throw "already declared". A class
 * expression has no name binding at the global level; only the window
 * property survives. Minifier-safe - no IIFE to strip. */
window.CardVariantPicker = window.CardVariantPicker || class extends HTMLElement {
  constructor() {
    super();
    this._init = this._init.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    console.log('[CVP] connectedCallback for picker', this);
    if (document.readyState === 'loading') {
      console.log('[CVP] deferring _init to DOMContentLoaded');
      document.addEventListener('DOMContentLoaded', this._init);
    } else {
      this._init();
    }
  }

  _init() {
    console.log('[CVP] _init starting');
    const variantScript = this.querySelector('script[data-variants]');
    if (!variantScript) { console.log('[CVP] no variantScript'); return; }
    try {
      this.variants = JSON.parse(variantScript.textContent);
    } catch (_) {
      return;
    }
    if (!Array.isArray(this.variants) || this.variants.length === 0) return;

    this.fieldsets = Array.from(this.querySelectorAll('[data-option-position]'));
    this.optionCount = this.fieldsets.length;

    // Seed selection from the buttons rendered with aria-pressed="true". If a
    // row has nothing pressed (shouldn't happen for the first paint), fall
    // back to the first available variant's options.
    this.selected = new Array(this.optionCount).fill(null);
    this.fieldsets.forEach((fs) => {
      const pos = parseInt(fs.dataset.optionPosition, 10);
      const pressed = fs.querySelector('[aria-pressed="true"]');
      if (pressed) this.selected[pos] = pressed.dataset.optionValue;
    });
    if (this.selected.some((v) => v === null)) {
      const fallback = this.variants.find((v) => v.available) || this.variants[0];
      fallback.options.forEach((val, i) => {
        if (this.selected[i] == null) this.selected[i] = val;
      });
      // Reflect the fallback in the UI so the user sees consistent state.
      this._reflectSelection();
    }

    console.log('[CVP] attaching click listener on picker', this, 'fieldsets=', this.fieldsets.length);
    this.addEventListener('click', this._onClick);
    this._refreshDisabled();
    this._syncForm();
  }

  _onClick(event) {
    console.log('[CVP] _onClick fired, target=', event.target?.tagName, event.target?.className);
    const btn = event.target.closest('[data-option-value]');
    console.log('[CVP] btn=', btn?.getAttribute('data-option-value'), 'disabled=', btn?.disabled);
    if (!btn) { console.log('[CVP] bail: no btn'); return; }
    event.preventDefault();
    const fs = btn.closest('[data-option-position]');
    if (!fs) { console.log('[CVP] bail: no fieldset'); return; }
    const pos = parseInt(fs.dataset.optionPosition, 10);
    console.log('[CVP] pos=', pos, 'this.selected before=', JSON.stringify(this.selected));
    this.selected[pos] = btn.dataset.optionValue;
    console.log('[CVP] this.selected after=', JSON.stringify(this.selected));
    this._reflectSelection();
    console.log('[CVP] reflected. thumb aria-pressed now=', btn.getAttribute('aria-pressed'));
    this._refreshDisabled();
    this._syncForm();
  }

  _reflectSelection() {
    this.fieldsets.forEach((fs) => {
      const pos = parseInt(fs.dataset.optionPosition, 10);
      const target = this.selected[pos];
      fs.querySelectorAll('[data-option-value]').forEach((b) => {
        b.setAttribute('aria-pressed', b.dataset.optionValue === target ? 'true' : 'false');
      });
      const selectedLabel = fs.querySelector('[data-selected-label]');
      if (selectedLabel && target != null) selectedLabel.textContent = target;
    });
  }

 /* For each option-row, disable values whose pick - combined with the
 * current selection in OTHER rows - yields no available variant. This is
   * the classic e-comm "grey out impossible combos" UX. */
  _refreshDisabled() {
    this.fieldsets.forEach((fs) => {
      const pos = parseInt(fs.dataset.optionPosition, 10);
      fs.querySelectorAll('[data-option-value]').forEach((b) => {
        const trial = this.selected.slice();
        trial[pos] = b.dataset.optionValue;
        const match = this.variants.find((v) => this._matches(v, trial));
        const dead = !match || match.available === false;
        if (dead) {
          b.setAttribute('disabled', '');
        } else {
          b.removeAttribute('disabled');
        }
      });
    });
  }

  _matches(variant, options) {
    for (let i = 0; i < this.optionCount; i++) {
      if (variant.options[i] !== options[i]) return false;
    }
    return true;
  }

  _currentVariant() {
    return this.variants.find((v) => this._matches(v, this.selected));
  }

  /* Push the currently-selected variant into the sibling <product-form>'s
   * hidden id input and update the price block. The form is identified by the
   * data-form-id on this element. */
  _syncForm() {
    const variant = this._currentVariant();
    // Picker now lives in .card__information; the form lives in .quick-add
    // (overlaid on the image). Both share the .card-wrapper ancestor.
    const formRoot = this.closest('.card-wrapper') || this.parentElement;
    if (!formRoot) return;

    const idInput = formRoot.querySelector('input[name="id"]');
    if (idInput && variant) {
      idInput.value = variant.id;
      if (variant.available) {
        idInput.removeAttribute('disabled');
      } else {
        idInput.setAttribute('disabled', '');
      }
    }

    // Toggle the Add-to-cart button state.
    const btn = formRoot.querySelector('button[type="submit"][name="add"]');
    if (btn) {
      if (variant && variant.available) {
        btn.removeAttribute('disabled');
        const label = btn.querySelector('span');
        if (label) label.textContent = label.dataset.addLabel || label.textContent;
      } else {
        btn.setAttribute('disabled', '');
        const label = btn.querySelector('span');
        if (label && label.dataset.soldOutLabel) {
          if (!label.dataset.addLabel) label.dataset.addLabel = label.textContent;
          label.textContent = label.dataset.soldOutLabel;
        }
      }
    }

    // Update the card's price block. If this picker is inside a Quick Add
    // mini-modal, scope the price update to that modal so the modal's own
    // price re-renders when the user changes Size. Otherwise scope to the
    // surrounding card-wrapper (default behaviour for inline pickers).
    const modal = this.closest('.card-mini-modal');
    const scope = modal || this.closest('.card-wrapper');
    if (scope && variant) {
      const priceBlock = scope.querySelector('.price');
      if (priceBlock) this._writePrice(priceBlock, variant);
      // Outside the modal, also swap the card's main image to the selected
      // variant's featured photo. The modal carries its own image and we
      // don't swap it (the user already saw it before opening Quick Add).
      if (!modal && variant.featured_image_url) {
        this._swapCardImage(scope, variant.featured_image_url);
      }
    }
  }

  _swapCardImage(cardWrapper, url) {
    // Target the primary product image. Theme renders both a primary and an
    // optional secondary hover-swap image under .card__media; only swap the
    // first one. .media__primary on Dawn-based themes; fall back to first img
    // inside .card__media.
    var img = cardWrapper.querySelector('.card__media .media img:not(.media__secondary), .card__media img');
    if (!img) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = url;
  }

  _writePrice(priceBlock, variant) {
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    priceBlock.classList.toggle('price--on-sale', onSale);

    // Update sale (active) price spans.
    const formatted = this._formatMoney(variant.price);
    priceBlock.querySelectorAll('.price-item--sale, .price-item--last, .price-item--regular').forEach((el) => {
      // The PDP/card markup uses different selectors per layout. We only
      // touch the elements whose text already looks like a price (to avoid
      // overwriting unrelated nodes).
      if (el.classList.contains('price-item--regular') && onSale) {
        el.textContent = this._formatMoney(variant.compare_at_price);
      } else if (!el.classList.contains('price-item--regular')) {
        el.textContent = formatted;
      } else if (!onSale) {
        el.textContent = formatted;
      }
    });
  }

  /* Mirror Shopify's `money` filter for the active currency. Cart.js exposes
   * `Shopify.formatMoney` if the storefront includes it; otherwise fall back to
   * the shop's own format, "€72,00" - euro sign, comma decimal, no currency
   * code. The code used to be appended here, which is why picking a size in
   * the quick-add modal turned a correct server-rendered "€72,00" back into
   * "€72,00 EUR" while every other price on the page stayed clean. */
  _formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return '€' + amount;
  }
}

if (!customElements.get('card-variant-picker')) {
  customElements.define('card-variant-picker', window.CardVariantPicker);
}
// build-marker 1779547900

/* forced rebuild 1779548293 */
